/**
 * Wegweiser zur Browser-Erlaubnis für Benachrichtigungen.
 *
 * Der Erlaubnis-Dialog des Browsers ist für die Nutzerinnen und Nutzer nicht
 * verlässlich sichtbar: Edge und Chrome behandeln die Anfrage standardmäßig
 * „leise" und zeigen statt eines Dialogs nur ein kleines, meist durchgestrichenes
 * Glocken-Symbol in der Adressleiste. Wer das nicht kennt, wird also NIE gefragt
 * und bekommt anschließend nie eine Benachrichtigung — ohne jeden Hinweis.
 *
 * Dieses Modul hält die reine Logik (Browsererkennung + Anleitungstexte) frei von
 * React, damit sie testbar bleibt. Die Texte richten sich an Menschen ohne
 * technischen Hintergrund: kurze, wörtlich nachvollziehbare Schritte.
 */

export type BrowserArt = "edge" | "chrome" | "firefox" | "safari" | "andere";

/** Was den Nutzer aktuell von funktionierenden Benachrichtigungen trennt. */
export type EinrichtungsZustand =
  /** Alles erledigt — Erlaubnis erteilt und Gerät angemeldet. */
  | "aktiv"
  /** Der Browser kann kein Web-Push (etwa ein privates Fenster). */
  | "nicht-unterstuetzt"
  /** iOS/iPadOS: Push gibt es nur in der zum Home-Bildschirm hinzugefügten Web-App. */
  | "ios-webapp-noetig"
  /** Noch nie gefragt — ein Klick genügt. */
  | "fragen-moeglich"
  /** Gefragt, aber der Browser hat die Nachfrage versteckt (leise Anfrage). */
  | "leise-abgefangen"
  /** Erlaubnis wurde verweigert oder blockiert — nur über die Einstellungen lösbar. */
  | "blockiert"
  /** Erlaubnis liegt vor, das Gerät konnte aber nicht angemeldet werden. */
  | "abo-fehlt";

export type Anleitung = {
  titel: string;
  /** Nummerierte Schritte, wörtlich nachvollziehbar. */
  schritte: string[];
  /** Beschriftung der Schaltfläche, falls eine Aktion sinnvoll ist. */
  aktion?: "erlauben" | "erneut-versuchen";
};

export const erkenneBrowser = (userAgent: string = navigator.userAgent): BrowserArt => {
  if (/Edg\//.test(userAgent)) return "edge"; // vor Chrome prüfen: Edge nennt sich auch "Chrome"
  if (/Firefox\/|FxiOS\//.test(userAgent)) return "firefox";
  if (/Chrome\/|Chromium\//.test(userAgent)) return "chrome";
  if (/Safari\//.test(userAgent)) return "safari";
  return "andere";
};

/** Wo die Erlaubnis je Browser von Hand umgestellt wird. */
const BLOCKIERT_SCHRITTE: Record<BrowserArt, string[]> = {
  edge: [
    "Klicke links in der Adressleiste auf das Schloss-Symbol.",
    "Wähle „Websiteeinstellungen“ (Zahnrad, öffnet einen neuen Tab).",
    "Stelle „Benachrichtigungen“ auf „Zulassen“.",
    "Komme zurück und lade diese Seite neu.",
  ],
  chrome: [
    "Klicke links in der Adressleiste auf das Schloss-Symbol.",
    "Wähle „Website-Einstellungen“.",
    "Stelle „Benachrichtigungen“ auf „Zulassen“.",
    "Komme zurück und lade diese Seite neu.",
  ],
  firefox: [
    "Klicke links in der Adressleiste auf das Schloss-Symbol.",
    "Klicke bei „Benachrichtigungen senden“ auf das Kreuz, um die Blockierung aufzuheben.",
    "Lade diese Seite neu.",
  ],
  safari: [
    "Öffne im Menü „Safari“ die Einstellungen.",
    "Wechsle auf „Websites“ und links auf „Mitteilungen“.",
    "Stelle diese Website auf „Erlauben“.",
    "Lade diese Seite neu.",
  ],
  andere: [
    "Öffne die Einstellungen deines Browsers für diese Website.",
    "Erlaube dort Benachrichtigungen.",
    "Lade diese Seite neu.",
  ],
};

/** Die leise Anfrage versteckt sich je Browser an derselben Stelle. */
const LEISE_SCHRITTE: string[] = [
  "Sieh rechts in der Adressleiste nach: dort erscheint ein kleines Glocken-Symbol, meist durchgestrichen.",
  "Klicke es an und wähle „Zulassen“ (bei Edge: „Immer zulassen“).",
  "Kommt kein Symbol, hebt die Anleitung unter „Blockiert“ die Sperre auf.",
];

/**
 * Text zum aktuellen Zustand — `null`, wenn nichts zu tun ist.
 * `browser` steuert nur die Formulierung, nicht die Logik.
 */
export const anleitungFuer = (browser: BrowserArt, zustand: EinrichtungsZustand): Anleitung | null => {
  switch (zustand) {
    case "aktiv":
      return null;
    case "fragen-moeglich":
      return {
        titel: "Benachrichtigungen einschalten",
        schritte: [
          "Klicke auf „Benachrichtigungen erlauben“.",
          "Bestätige die Nachfrage des Browsers mit „Zulassen“.",
        ],
        aktion: "erlauben",
      };
    case "leise-abgefangen":
      return {
        titel: "Der Browser hat die Nachfrage versteckt",
        schritte: LEISE_SCHRITTE,
        aktion: "erlauben",
      };
    case "blockiert":
      return {
        titel: "Benachrichtigungen sind für KMVS gesperrt",
        schritte: BLOCKIERT_SCHRITTE[browser],
      };
    case "abo-fehlt":
      return {
        titel: "Dieses Gerät ist nicht angemeldet",
        schritte: [
          "Klicke auf „Erneut versuchen“.",
          "Bleibt es dabei, erreicht dieser Rechner den Push-Dienst nicht — das sperrt in der Regel das Firmennetz. Bitte an die IT melden.",
        ],
        aktion: "erneut-versuchen",
      };
    case "ios-webapp-noetig":
      return {
        titel: "Als App zum Home-Bildschirm hinzufügen",
        schritte: [
          "Tippe unten auf das Teilen-Symbol (Quadrat mit Pfeil nach oben).",
          "Wähle „Zum Home-Bildschirm“.",
          "Öffne KMVS künftig über dieses Symbol — nur dort sind Benachrichtigungen möglich.",
        ],
      };
    case "nicht-unterstuetzt":
      return {
        titel: "Dieser Browser kann keine Benachrichtigungen",
        schritte: [
          "Ein privates Fenster verhindert Benachrichtigungen — öffne KMVS in einem normalen Fenster.",
          "Hilft das nicht, nutze Microsoft Edge, Chrome oder Firefox.",
        ],
      };
  }
};

/** Kurzer Satz für die Kopfzeile des Hinweises — erklärt den Nutzen, nicht die Technik. */
export const NUTZEN_SATZ =
  "Damit meldet sich KMVS bei Erwähnungen, neuen Aufgaben und Fristen auch dann, wenn das Fenster im Hintergrund liegt.";
